import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { RouteComponentProps, withRouter } from 'react-router';

import { getChecklistOptions } from '../../../api';
import { ChecklistOptions } from '../../../api/configurations/checklist/types';
import { SHADOW_PROJECT_TYPE } from '../../../constants';
import { SettingsContext } from '../../../contexts/settings';
import { useNotifications } from '../../../hooks/useNotifications';
import { RouteParams } from '../../../routes/types';
import { Preloader } from '../../ui';
import CheckListCard from './CheckListCard';
import { CheckListHeader } from './CheckListHeader';
import { checkListOptions, shadowLoginOptions } from './settings';
import { CheckListWrapper, RestSettingsSubtitle } from './style';

// Module-level cache to track in-flight requests (persists across component instances)
const inFlightRequests = new Map<string, Promise<ChecklistOptions>>();

const CheckList = ({ match }: RouteComponentProps): JSX.Element => {
  const { projectId } = match.params as RouteParams;
  const [fetching, setFetching] = useState<boolean>(false);
  const { showErrorNotify } = useNotifications();

  const intl = useIntl();

  const { checklistItems, setChecklistItems, currentProject } = useContext(SettingsContext);

  useEffect(() => {
    if (checklistItems) {
      return;
    }

    const processResponse = (res: ChecklistOptions) => {
      const preparedOptions = res.reduce((acc, item) => ({ ...acc, [item.step]: item.status }), {});
      setChecklistItems(preparedOptions);
    };

    // Checking if there's already an in-flight request for this project
    const existingRequest = inFlightRequests.get(projectId);
    if (existingRequest) {
      setFetching(true);
      existingRequest
        .then(processResponse)
        .catch((err) => showErrorNotify(err))
        .finally(() => setFetching(false));
      return;
    }

    // Creating new request and caching it
    setFetching(true);
    const request = getChecklistOptions(projectId);
    inFlightRequests.set(projectId, request);

    request
      .then(processResponse)
      .catch((err) => showErrorNotify(err))
      .finally(() => {
        setFetching(false);
        inFlightRequests.delete(projectId);
      });
  }, [currentProject]);

  useEffect(() => () => setChecklistItems(null), []);

  const totalCompletedSteps: number = useMemo(() => {
    if (!checklistItems) {
      return 0;
    }
    return Object.values(checklistItems).filter((i) => i).length;
  }, [checklistItems]);

  if (!currentProject || !checklistItems || fetching) {
    return (
      <CheckListWrapper>
        <Preloader />
      </CheckListWrapper>
    );
  }

  if (currentProject && currentProject.type === SHADOW_PROJECT_TYPE) {
    return (
      <CheckListWrapper>
        <CheckListHeader completedSteps={totalCompletedSteps} />
        <div>
          <CheckListCard
            checklistItems={checklistItems}
            options={shadowLoginOptions}
            name="login-method"
            isOpened
          />
          <RestSettingsSubtitle>
            {intl.messages['check-list.rest-steps.not-required']}
          </RestSettingsSubtitle>
          <CheckListCard
            checklistItems={checklistItems}
            options={shadowLoginOptions}
            name="secure"
          />
        </div>
      </CheckListWrapper>
    );
  }

  return (
    <CheckListWrapper>
      <CheckListHeader completedSteps={totalCompletedSteps} />
      <div>
        <CheckListCard
          checklistItems={checklistItems}
          options={checkListOptions}
          name="login-method"
          isOpened
        />
        <RestSettingsSubtitle>
          {intl.messages['check-list.rest-steps.not-required']}
        </RestSettingsSubtitle>
        <CheckListCard checklistItems={checklistItems} options={checkListOptions} name="secure" />
        <CheckListCard
          checklistItems={checklistItems}
          options={checkListOptions}
          name="authentication"
        />
        <CheckListCard
          checklistItems={checklistItems}
          options={checkListOptions}
          name="user-data-base"
        />
        <CheckListCard
          checklistItems={checklistItems}
          options={checkListOptions}
          name="customization"
        />
      </div>
      {/* <Notification icon={CogDouble}>
        <Button style={{ textTransform: 'uppercase' }} look="link" palette="brand" size="md">
          {intl.messages['check-list.rest-steps.open-settings']}
        </Button>
        <OpenSettingsDescription>
          {intl.messages['check-list.rest-steps.open-settings.description']}
        </OpenSettingsDescription>
      </Notification> */}
    </CheckListWrapper>
  );
};

export default withRouter(CheckList);
